import { useSelector } from 'react-redux';
import { getActiveUser } from '../services/user/redux/selectors';
import { useMemo } from 'react';
import { ServiceTier } from '../services/user/types';
import { FeatureAccessKey } from '../types/featureAccess';

export type FeatureAccessConfig = Record<FeatureAccessKey, boolean>;

// Product name constant for Drive-Warehouse feature gating
const DRIVE_WAREHOUSE_PRODUCT = 'Drive-Warehouse' as const;

const FEATURE_ACCESS_CONFIG: Record<ServiceTier, FeatureAccessConfig> = {
  [ServiceTier.ESSENTIALS]: {
    canAccessSales: false,
    canAccessCustomers: true,
    canAccessPurchasing: false,
    canAccessAccounting: true,
    canAccessIntegrations: true,
    canAccessTransferOrders: false,
    canAccessPaymentTerms: false,
    canAccessRepresentatives: false,
    canAccessTaxRates: false,
    canAccessCarriers: false,
    canAccessSettings: true,
    canViewTotalItemSalesReport: false,
    canViewReorderReport: true,
    canViewCustomerHistoryReport: false,
    canViewItemPriceListReport: false,
    canViewMarginReport: false,
    canViewPackingListReport: false,
    canViewPickReport: false,
    canViewPurchaseOrderReport: false,
    canViewPurchaseOrderSummaryReport: false,
    canViewReceivingListReport: false,
    canViewSalesOrderReport: false,
    canViewSalesOrderSummaryReport: false,
    canViewShippingInvoiceReport: false,
    canViewVendorFulfillmentHistoryReport: false,
    canAccessReports: false,
    canAccessTemplates: false,
    canAccessAiInsights: false,
    canViewRestrictedPermissions: false,
    canViewRestrictedPreferences: false,
  },
  [ServiceTier.GROWTH]: {
    canAccessSales: true,
    canAccessCustomers: true,
    canAccessPurchasing: true,
    canAccessAccounting: true,
    canAccessIntegrations: true,
    canAccessTransferOrders: false,
    canAccessPaymentTerms: true,
    canAccessRepresentatives: true,
    canAccessTaxRates: true,
    canAccessCarriers: true,
    canAccessSettings: true,
    canViewTotalItemSalesReport: true,
    canViewReorderReport: true,
    canAccessReports: true,
    canViewCustomerHistoryReport: true,
    canViewItemPriceListReport: true,
    canViewMarginReport: true,
    canViewPackingListReport: true,
    canViewPickReport: true,
    canViewPurchaseOrderReport: true,
    canViewPurchaseOrderSummaryReport: true,
    canViewReceivingListReport: true,
    canViewSalesOrderReport: true,
    canViewSalesOrderSummaryReport: true,
    canViewShippingInvoiceReport: true,
    canViewVendorFulfillmentHistoryReport: true,
    canAccessTemplates: false,
    canAccessAiInsights: true,
    canViewRestrictedPermissions: true,
    canViewRestrictedPreferences: true,
  },
  [ServiceTier.SCALE]: {
    canAccessSales: true,
    canAccessCustomers: true,
    canAccessPurchasing: true,
    canAccessAccounting: true,
    canAccessIntegrations: true,
    canAccessTransferOrders: true,
    canAccessPaymentTerms: true,
    canAccessRepresentatives: true,
    canAccessTaxRates: true,
    canAccessCarriers: true,
    canAccessSettings: true,
    canViewTotalItemSalesReport: true,
    canViewReorderReport: true,
    canAccessReports: true,
    canViewCustomerHistoryReport: true,
    canViewItemPriceListReport: true,
    canViewMarginReport: true,
    canViewPackingListReport: true,
    canViewPickReport: true,
    canViewPurchaseOrderReport: true,
    canViewPurchaseOrderSummaryReport: true,
    canViewReceivingListReport: true,
    canViewSalesOrderReport: true,
    canViewSalesOrderSummaryReport: true,
    canViewShippingInvoiceReport: true,
    canViewVendorFulfillmentHistoryReport: true,
    canAccessTemplates: true,
    canAccessAiInsights: true,
    canViewRestrictedPermissions: true,
    canViewRestrictedPreferences: true,
  },
} as const;

/**
 * Hook for accessing the current user's service tier information
 */
export const useServiceTier = (): {
  serviceTier: ServiceTier | null;
  productName: string | null;
} => {
  const activeUserData = useSelector(getActiveUser);

  const serviceTier = activeUserData?.serviceTier || null;
  const productName = activeUserData?.productName || null;

  return {
    serviceTier,
    productName,
  };
};

/**
 * Check if a service tier is valid and exists in our configuration
 */
const isValidServiceTier = (
  serviceTier: ServiceTier | null
): serviceTier is ServiceTier => {
  return (
    serviceTier !== null && Object.values(ServiceTier).includes(serviceTier)
  );
};

/**
 * Hook for checking feature access based on service tier
 * Legacy users get full access for backward compatibility
 * Only applies feature gating to Drive-Warehouse products
 */
export const useFeatureAccess = (): FeatureAccessConfig => {
  const { serviceTier, productName } = useServiceTier();

  return useMemo(() => {
    const isDriveWarehouseProduct = productName === DRIVE_WAREHOUSE_PRODUCT;
    const hasValidServiceTier = isValidServiceTier(serviceTier);

    // Only apply feature gating to Drive-Warehouse users with valid service tiers
    if (isDriveWarehouseProduct && hasValidServiceTier) {
      return FEATURE_ACCESS_CONFIG[serviceTier];
    }

    // Default to full access (SCALE tier) for legacy users, invalid tiers, or other products
    return { ...FEATURE_ACCESS_CONFIG[ServiceTier.SCALE] };
  }, [serviceTier, productName]);
};
